import React from 'react';
import { Clip, DialogueLine, EntryTake } from '../../types';
import { SoulClipItem } from './studioUtils';
import {
  Sparkles,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  Trash2,
  Scissors,
  MessageSquare,
  Mic,
  Share2
} from 'lucide-react';
import { ShareSoulClipModal } from '../share/ShareSoulClipModal';

interface SoulClipsVaultDrawerProps {
  isVaultOpen: boolean;
  setIsVaultOpen: (open: boolean) => void;
  vaultClips: SoulClipItem[];
  filteredVaultClips: SoulClipItem[];
  currentClip: Clip | null;
  userName?: string;
  duration: number;
  takes: EntryTake[];
  dialogueLines: DialogueLine[];
  isLoadingVault: boolean;
  deletingClipId: string | null;
  vaultFilter: 'all' | 'takes' | 'stems' | 'presets';
  setVaultFilter: (filter: 'all' | 'takes' | 'stems' | 'presets') => void;
  vaultSearch: string;
  setVaultSearch: (query: string) => void;
  refreshSoulClips: () => void;
  handleSelectSoulClip: (clipItem: SoulClipItem) => void;
  handleDeleteSoulClip: (e: React.MouseEvent, clipItem: SoulClipItem) => void;
  onBackToCapture: () => void;
  onShareClipToSoulShare?: (clipItem: SoulClipItem) => void;
}

export const SoulClipsVaultDrawer: React.FC<SoulClipsVaultDrawerProps> = ({
  isVaultOpen,
  setIsVaultOpen,
  vaultClips,
  filteredVaultClips,
  currentClip,
  userName,
  duration,
  takes,
  dialogueLines,
  isLoadingVault,
  deletingClipId,
  vaultFilter,
  setVaultFilter,
  vaultSearch,
  setVaultSearch,
  refreshSoulClips,
  handleSelectSoulClip,
  handleDeleteSoulClip,
  onBackToCapture,
  onShareClipToSoulShare
}) => {
  const [sharingClip, setSharingClip] = React.useState<SoulClipItem | null>(null);
  const scrollRowRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = scrollRowRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      const rawDelta = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      if (rawDelta === 0) return;
      if (el.scrollWidth > el.clientWidth) {
        e.preventDefault();
        const baseDelta = e.deltaMode === 1 ? rawDelta * 32 : (e.deltaMode === 2 ? rawDelta * el.clientWidth : rawDelta);
        // Multiplier to increase scroll distance per mouse wheel movement (~1 card per notch)
        const scrollMultiplier = 2.5;
        el.scrollLeft += baseDelta * scrollMultiplier;
      }
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', handleWheel);
    };
  }, [isVaultOpen, filteredVaultClips.length]);

  const handleOpenShareModal = (e: React.MouseEvent, clip: SoulClipItem) => {
    e.stopPropagation();
    if (onShareClipToSoulShare) {
      onShareClipToSoulShare(clip);
    } else {
      setSharingClip(clip);
    }
  };

  return (
    <div className="soulclips-vault-container" style={{ marginBottom: '1.25rem', width: '100%' }}>
      {/* ── Summary / Top Bar Mode (Always Visible on Top of Studio) ── */}
      <div
        className="soulclips-vault-bar"
        onClick={() => {
          const nextState = !isVaultOpen;
          setIsVaultOpen(nextState);
          sessionStorage.setItem('soulsync_vault_open', String(nextState));
        }}
      >
        <div className="vault-brand">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Sparkles size={18} color="var(--accent-purple)" />
            <h1 style={{ fontSize: '1.15rem', fontWeight: 800, margin: 0, color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '6px' }}>
              SoulClips Vault
            </h1>
          </div>
          <span className={`vault-badge ${userName ? 'vault-badge-purple' : 'vault-badge-green'}`}>
            🎬 {userName ? `${vaultClips.length} Captures` : `Temp Vault (${vaultClips.length}/3 Clips)`}
          </span>
        </div>

        <div className="vault-active-summary" style={{ display: 'none' }}>
          <span style={{ color: '#c4b5fd', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: currentClip && currentClip.id ? '#10b981' : '#64748b',
                display: 'inline-block'
              }}
            />
            Active:
          </span>
          <span
            style={{
              color: currentClip && currentClip.id ? '#ffffff' : 'var(--text-muted)',
              fontWeight: 600,
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {currentClip && currentClip.id ? currentClip.title : 'None Loaded'}
          </span>
          <span style={{ color: 'var(--text-muted)' }}>•</span>
          <span style={{ color: '#94a3b8' }}>
            ⏱ {duration.toFixed(1)}s
          </span>
          <span style={{ color: 'var(--text-muted)' }}>•</span>
          <span style={{ color: takes.length > 0 ? '#6ee7b7' : 'var(--text-muted)' }}>
            🎤 {takes.length} Takes
          </span>
          <span style={{ color: 'var(--text-muted)' }}>•</span>
          <span style={{ color: '#38bdf8' }}>
            💬 {dialogueLines.length} Lines
          </span>

          {/* Quick Delete Active Clip button if not a preset and clip exists */}
          {Boolean(currentClip && currentClip.id && !currentClip.isPreset) && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const matched = vaultClips.find((c) => c.id === currentClip!.id);
                if (matched) {
                  handleDeleteSoulClip(e, matched);
                }
              }}
              style={{
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.35)',
                borderRadius: '6px',
                color: '#f87171',
                padding: '2px 8px',
                fontSize: '0.72rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                marginLeft: '4px'
              }}
              title={`Delete active clip "${currentClip!.title}"`}
            >
              <Trash2 size={11} />
              <span>Delete Active</span>
            </button>
          )}
        </div>

        <div className="vault-controls" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={refreshSoulClips}
            disabled={isLoadingVault}
            className="btn-secondary"
            style={{ padding: '4px 10px', fontSize: '0.74rem', display: 'flex', alignItems: 'center', gap: '5px' }}
            title="Refresh captures from Cloudflare R2 and D1"
          >
            <RefreshCw size={12} className={isLoadingVault ? 'spin' : ''} />
            <span>{isLoadingVault ? 'Syncing...' : 'Sync'}</span>
          </button>

          <button
            type="button"
            onClick={() => {
              const nextState = !isVaultOpen;
              setIsVaultOpen(nextState);
              sessionStorage.setItem('soulsync_vault_open', String(nextState));
            }}
            className="vault-twirl-btn"
            style={{ padding: '4px 12px', fontSize: '0.76rem' }}
          >
            <span>{isVaultOpen ? 'Close Vault' : 'Browse SoulClips'}</span>
            {isVaultOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {/* Twirl Open Drawer / Tray */}
      {isVaultOpen && (
        <div className="soulclips-vault-drawer">
          {/* Toolbar: Filter Pills & Search */}
          <div className="vault-drawer-toolbar">
            <div className="vault-filter-group">
              <button
                type="button"
                onClick={() => setVaultFilter('all')}
                className={`vault-filter-btn ${vaultFilter === 'all' ? 'active' : ''}`}
              >
                All SoulClips ({vaultClips.length})
              </button>
              <button
                type="button"
                onClick={() => setVaultFilter('takes')}
                className={`vault-filter-btn ${vaultFilter === 'takes' ? 'active' : ''}`}
              >
                🎤 With Takes ({vaultClips.filter((c) => (c.takeCount || 0) > 0).length})
              </button>
              <button
                type="button"
                onClick={() => setVaultFilter('stems')}
                className={`vault-filter-btn ${vaultFilter === 'stems' ? 'active' : ''}`}
              >
                🎵 Stems Ready ({vaultClips.filter((c) => c.hasStems).length})
              </button>
              {vaultClips.some((c) => c.isPreset) && (
                <button
                  type="button"
                  onClick={() => setVaultFilter('presets')}
                  className={`vault-filter-btn ${vaultFilter === 'presets' ? 'active' : ''}`}
                >
                  ⭐ Presets ({vaultClips.filter((c) => c.isPreset).length})
                </button>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="text"
                placeholder="Search captures..."
                value={vaultSearch}
                onChange={(e) => setVaultSearch(e.target.value)}
                style={{
                  background: 'rgba(0, 0, 0, 0.4)',
                  border: '1px solid var(--border-glass)',
                  borderRadius: 'var(--radius-full)',
                  padding: '4px 12px',
                  fontSize: '0.74rem',
                  color: '#ffffff',
                  outline: 'none',
                  width: '150px'
                }}
              />
            </div>
          </div>

          {/* Horizontal Scroll Cards Row */}
          <div ref={scrollRowRef} className="soulclips-scroll-row">
            {filteredVaultClips.length === 0 ? (
              <div style={{ padding: '1.5rem', textAlign: 'center', width: '100%', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                No SoulClips found matching filter. Capture a scene in SoulCapture to get started!
              </div>
            ) : (
              filteredVaultClips.map((clipItem) => {
                const isActive = Boolean(currentClip && clipItem.id === currentClip.id);
                return (
                  <div
                    key={clipItem.id}
                    className={`soulclip-card ${isActive ? 'is-active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelectSoulClip(clipItem);
                    }}
                    title={`Click to populate room tools with "${clipItem.title}"`}
                  >
                    {/* Card Poster */}
                    <div className="soulclip-card-poster">
                      <img
                        src={clipItem.posterUrl}
                        alt={clipItem.title}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=480&q=80';
                        }}
                      />
                      {/* Duration Badge */}
                      <div style={{ position: 'absolute', bottom: '6px', right: '6px', background: 'rgba(0,0,0,0.8)', color: '#fff', fontSize: '0.66rem', padding: '1px 5px', borderRadius: '4px', fontWeight: 700 }}>
                        {clipItem.duration}s
                      </div>
                      {/* Active Tag */}
                      {isActive && (
                        <div style={{ position: 'absolute', top: '6px', left: '6px', background: 'var(--accent-purple)', color: '#fff', fontSize: '0.62rem', padding: '1px 6px', borderRadius: 'var(--radius-full)', fontWeight: 800, letterSpacing: '0.04em' }}>
                          ACTIVE
                        </div>
                      )}
                      {!clipItem.isPreset && (
                        <div style={{ position: 'absolute', top: '6px', right: '34px', background: 'rgba(16, 185, 129, 0.85)', color: '#fff', fontSize: '0.62rem', padding: '1px 5px', borderRadius: '4px', fontWeight: 700 }}>
                          R2 CLOUD
                        </div>
                      )}

                      {/* Share Status Badge */}
                      {clipItem.isShared && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '6px',
                            right: !clipItem.isPreset ? '92px' : '34px',
                            background: clipItem.shareVisibility === 'public'
                              ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(37, 99, 235, 0.9) 100%)'
                              : 'linear-gradient(135deg, rgba(168, 85, 247, 0.9) 0%, rgba(147, 51, 234, 0.9) 100%)',
                            color: '#fff',
                            fontSize: '0.58rem',
                            padding: '1px 5px',
                            borderRadius: '4px',
                            fontWeight: 800,
                            letterSpacing: '0.03em',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '3px',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                          }}
                          title={clipItem.shareVisibility === 'public' ? 'Shared publicly with SoulSync community' : `Shared directly with friends`}
                        >
                          <span>{clipItem.shareVisibility === 'public' ? '🌐 PUBLIC' : '👥 DIRECT'}</span>
                        </div>
                      )}

                      {/* Garbage Delete Icon Button */}
                      <button
                        type="button"
                        className="soulclip-delete-btn"
                        disabled={deletingClipId === clipItem.id}
                        onClick={(e) => handleDeleteSoulClip(e, clipItem)}
                        title={`Delete "${clipItem.title}" from SoulClips Vault`}
                      >
                        {deletingClipId === clipItem.id ? (
                          <RefreshCw size={11} className="spin" />
                        ) : (
                          <Trash2 size={12} />
                        )}
                      </button>
                    </div>

                    {/* Card Body */}
                    <div className="soulclip-card-body">
                      <h4 className="soulclip-card-title">{clipItem.title}</h4>

                      <div className="soulclip-card-meta">
                        {/* Trim range */}
                        <div className="soulclip-meta-row">
                          <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#94a3b8' }}>
                            <Scissors size={10} color="#a78bfa" />
                            Trim: {clipItem.trimStart.toFixed(1)}s - {clipItem.trimEnd.toFixed(1)}s
                          </span>
                        </div>

                        {/* Diarization & Takes Status */}
                        <div className="soulclip-meta-row" style={{ marginTop: '2px' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#94a3b8' }}>
                            <MessageSquare size={10} color="#38bdf8" />
                            {clipItem.dialogueCount || (isActive ? dialogueLines.length : 0)} Lines
                          </span>

                          <span
                            className="soulclip-mini-badge"
                            style={{
                              background: (clipItem.takeCount || 0) > 0 || (isActive && takes.length > 0)
                                ? 'rgba(16, 185, 129, 0.2)'
                                : 'rgba(255, 255, 255, 0.08)',
                              color: (clipItem.takeCount || 0) > 0 || (isActive && takes.length > 0)
                                ? '#6ee7b7'
                                : 'var(--text-muted)'
                            }}
                          >
                            <Mic size={9} />
                            {(clipItem.takeCount || 0) > 0 ? `${clipItem.takeCount} Takes` : (isActive && takes.length > 0 ? `${takes.length} Takes` : '0 Takes')}
                          </span>
                        </div>

                        {/* Action buttons (Share & Delete) */}
                        <div className="soulclip-meta-row" style={{ marginTop: '2px', paddingTop: '4px', borderTop: '1px solid rgba(255,255,255,0.06)', justifyContent: 'flex-end' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {/* Share button */}
                            <button
                              type="button"
                              onClick={(e) => handleOpenShareModal(e, clipItem)}
                              style={{
                                background: clipItem.isShared ? 'rgba(59, 130, 246, 0.16)' : 'rgba(255, 255, 255, 0.08)',
                                border: clipItem.isShared ? '1px solid rgba(59, 130, 246, 0.45)' : '1px solid rgba(255, 255, 255, 0.15)',
                                color: clipItem.isShared ? '#93c5fd' : '#cbd5e1',
                                fontSize: '0.66rem',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '3px',
                                cursor: 'pointer',
                                padding: '1px 6px',
                                borderRadius: '4px',
                                transition: 'all 0.15s ease'
                              }}
                              title={clipItem.isShared ? 'Manage community or friend sharing' : 'Share this clip'}
                            >
                              <Share2 size={9} />
                              <span>{clipItem.isShared ? 'Shared' : 'Share'}</span>
                            </button>

                            {/* Delete button */}
                            <button
                              type="button"
                              onClick={(e) => handleDeleteSoulClip(e, clipItem)}
                              disabled={deletingClipId === clipItem.id}
                              style={{
                                background: 'rgba(239, 68, 68, 0.12)',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                color: '#f87171',
                                fontSize: '0.66rem',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '3px',
                                cursor: 'pointer',
                                padding: '1px 6px',
                                borderRadius: '4px',
                                transition: 'all 0.15s ease'
                              }}
                              title={`Delete "${clipItem.title}" from vault`}
                            >
                              <Trash2 size={9} />
                              <span>Delete</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Modal for sharing clip */}
      <ShareSoulClipModal
        isOpen={Boolean(sharingClip)}
        onClose={() => setSharingClip(null)}
        clip={sharingClip}
        takes={sharingClip?.id === currentClip?.id ? takes : (sharingClip as any)?.takes}
        currentUserName={userName}
        onShareUpdated={(updatedClip) => {
          refreshSoulClips();
          setSharingClip(null);
        }}
      />
    </div>
  );
};
